import { Injectable, inject } from '@angular/core';
import { CanActivate, Router, UrlTree, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, take, filter, switchMap, catchError, timeout } from 'rxjs/operators';
import { LoggerService } from '@app/core/infrastructure';
import { UserFacade } from '@app/store/user/user.facade';
import { UserRole } from '@app/core/api/user';

/**
 * Guard that restricts access based on user role.
 * 
 * Usage in routes:
 * ```ts
 * {
 *   path: 'manager/feedback',
 *   canActivate: [AuthGuard, RoleGuard],
 *   data: { roles: ['manager', 'admin'] },
 *   loadChildren: () => import('./pages/manager/...').then(m => m.Module)
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {
  private readonly logger = inject(LoggerService).scope('RoleGuard');

  constructor(
    private userFacade: UserFacade,
    private router: Router
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // Get required roles from route data
    const requiredRoles: UserRole[] = route.data['roles'] || [];

    if (requiredRoles.length === 0) {
      this.logger.warn('RoleGuard: No roles specified in route data, allowing access');
      return of(true);
    }

    // Wait for profile to be loaded before checking role
    return this.userFacade.isProfileLoaded$.pipe(
      filter(Boolean),
      take(1),
      timeout(5000),
      switchMap(() => this.userFacade.userRole$),
      take(1),
      map(userRole => {
        const hasPermission = requiredRoles.includes(userRole);

        this.logger.debug('Role check:', {
          userRole,
          requiredRoles,
          hasPermission
        });

        if (hasPermission) {
          return true;
        }

        // Redirect to home if user doesn't have required role
        this.logger.debug('Insufficient permissions, redirecting to /home');
        return this.router.createUrlTree(['/home']);
      }),
      catchError((error) => {
        this.logger.error('Error during role check:', error);
        // Redirect to home on error
        return of(this.router.createUrlTree(['/home']));
      })
    );
  }
}
